import axios from 'axios';
import { i18n } from '@/plugins/i18n';
import { businessIntelligenceService } from '@/services/business-intelligence.service';
import { businessIntelligenceSuppliersService } from '@/services/businessIntelligenceSuppliers.service';
import { tenantSettingsService } from '@/services/tenant-settings.service';
import { tendersService } from '@/services/tenders.service';

const DEFAULT_PAGINATION = {
  page: 1,
  totalCount: 0,
  totalPages: 0,
};

const initialState = () => ({
  charts: [],
  dataPointValues: null,
  performanceSummary: null,
  competitor: null,
  competitorPerformanceSummary: null,
  tender: null,
  // Tender and id of the latest BI tender request; a response from any earlier request is dropped.
  tenderId: null,
  tenderRequestId: 0,
  tendersBidded: {
    pending: false,
    matchesPending: false,
    data: [],
    // A phone's list adds the next page under the listed tenders.
    loadingMore: false,
    loadMoreError: null,
  },
  competitors: [],
  // Emitters (procuring entities) are a catalogue shared by every tenant, paginated by the API.
  emitters: [],
  emittersPagination: DEFAULT_PAGINATION,
  // A phone's list adds the next page under the listed emitters.
  emittersLoadingMore: false,
  emittersLoadMoreError: null,
  emitter: null,
  emitterPerformanceSummary: null,
  // Set once getAllCompetitors has loaded; cleared whenever the list changes so it is refetched.
  allCompetitorsLoaded: false,
  // Bumped on every invalidation so a fetch started before it cannot mark a stale list as loaded.
  competitorsGeneration: 0,
  pagination: DEFAULT_PAGINATION,
  sortingField: 'date',
  sortingOrder: 'desc',
  dates: [],
  status: {
    error: null,
    pending: false,
    dataPointDataPending: false,
    resolved: false,
    rejected: false,
    detailedPending: false,
    // Tracks the emitters list only, so the dialogs' `pending` does not blank the table.
    emittersPending: false,
    // `pending` is shared by every request of this module; this one tracks the BI tender only.
    tenderPending: false,
    tenderError: null,
  },
});

const state = initialState();

// In-flight getAllCompetitors request, shared by concurrent callers, and the generation it was started for.
let allCompetitorsRequest = null;
let allCompetitorsRequestGeneration = 0;
// Never reset, so a late response cannot collide with a request made after a store reset.
let nextTenderRequestId = 1;
// Bumped by every tenders-bidded search, so a reload of the listed pages started before it stops.
let tendersBiddedSearch = 0;
// The same for the emitters.
let emittersSearch = 0;

// One page of the tenders bidded on, an emitter's, a competitor's or the tenant's, in the order picked.
const fetchTendersBidded = (
  { startDate, endDate, status, page, competitorId, emitterId },
  { sortingField, sortingOrder },
) => {
  if (emitterId) {
    return businessIntelligenceService.getEmitterTenders(
      emitterId,
      startDate,
      endDate,
      status,
      page,
      sortingField,
      sortingOrder,
    );
  }
  if (competitorId) {
    return businessIntelligenceService.getCompetitorTendersBidded(
      competitorId,
      startDate,
      endDate,
      status,
      page,
      sortingField,
      sortingOrder,
    );
  }
  return businessIntelligenceService.getTendersBidded(startDate, endDate, status, page, sortingField, sortingOrder);
};

// Each once, the listed first: a tender changed since the previous page can shift the pages.
const withoutListed = (listed, tenders) => {
  const ids = new Set(listed.map((tender) => tender.id));
  return [...listed, ...tenders.filter(({ id }) => !ids.has(id))];
};

const invalidateCompetitors = (state) => {
  state.allCompetitorsLoaded = false;
  state.competitorsGeneration += 1;
};

const actions = {
  async getSelectedCharts({ commit }, type) {
    return businessIntelligenceService.getCharts(type).then((response) => {
      commit('GET_CHART_SUCCESS', { chart: response });
    });
  },
  async getAllCharts({ commit }) {
    commit('GET_CHART');

    const chartsList = [
      'win_amount_per_year',
      'bid_amount_per_year',
      'cumul_amount_provider',
      'weighted_diff_per_month',
    ];
    Promise.all(chartsList.map((chartType) => businessIntelligenceService.getCharts(chartType)))
      .then((chartDataArray) => {
        chartDataArray.forEach((chart, index) => {
          chart.id = formatId(chartsList[index]);
        });
        commit('GET_CHART_SUCCESS', { charts: chartDataArray });
      })
      .catch((error) => {
        console.error('Error fetching chart data:', error);
      });
  },

  async getDataFromDataPoint({ commit }, { supplier, month, year }) {
    commit('GET_DATAPOINT_VALUES');
    return businessIntelligenceSuppliersService
      .getSupplierAwards({ supplier, month, year })
      .then((values) => {
        commit('GET_DATAPOINT_VALUES_SUCCESS', values);
      })
      .catch((error) => {
        console.error(`Error fetching data point's data:`, error);
      });
  },

  async getPerformanceSummary({ commit }, { startDate, endDate }) {
    commit('GET_PERFORMANCE_SUMMARY');
    return businessIntelligenceService
      .getPerformanceSummary(startDate, endDate)
      .then((values) => {
        commit('GET_PERFORMANCE_SUMMARY_SUCCESS', values);
      })
      .catch((error) => {
        console.error(`Error fetching performance summary:`, error);
        commit('ERROR', error);
        throw error;
      });
  },

  async getCompetitorPerformanceSummary({ commit }, { competitorId, startDate, endDate }) {
    commit('GET_COMPETITOR_PERFORMANCE_SUMMARY');
    return businessIntelligenceService
      .getCompetitorPerformanceSummary(competitorId, startDate, endDate)
      .then((values) => {
        commit('GET_COMPETITOR_PERFORMANCE_SUMMARY_SUCCESS', values);
      })
      .catch((error) => {
        console.error(`Error fetching competitor performance summary:`, error);
        commit('ERROR', error);
        throw error;
      });
  },

  async getTendersBidded({ commit, dispatch, state }, query) {
    tendersBiddedSearch += 1;
    commit('GET_TENDERS_BIDDED');
    commit('RESET_PAGINATION');

    return fetchTendersBidded(query, state)
      .then((values) => {
        commit('GET_TENDERS_BIDDED_SUCCESS', values);
        if (values.data && values.data.length > 0) {
          dispatch('getTendersBiddedMatches', { tenders: values.data });
        } else {
          commit('SET_TENDERS_BIDDED_MATCHES_PENDING', false);
        }
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching tenders bidded:`, error);
          throw error;
        }
      });
  },
  // Adds the next page under the listed tenders (a phone's list). Not `pending`: the list stays
  // usable while it loads, and a failure keeps what's listed.
  loadMoreTendersBidded({ commit, dispatch, state }, query) {
    commit('LOAD_MORE_TENDERS_BIDDED');
    return fetchTendersBidded(query, state).then(
      (values) => {
        commit('APPEND_TENDERS_BIDDED', values);
        // With the listed tenders whose matches this request cancelled (they share a cancel token).
        const unmatched = state.tendersBidded.data.filter((tender) => !tender.matchedKeywords);
        if (unmatched.length) dispatch('getTendersBiddedMatches', { tenders: unmatched });
      },
      (error) => {
        // A new search cancels it, and replaces the list anyway.
        if (axios.isCancel(error)) commit('LOAD_MORE_TENDERS_BIDDED_CANCELLED');
        else commit('LOAD_MORE_TENDERS_BIDDED_FAILED', error);
      },
    );
  },
  // Reloads every page a phone's list shows, one after the other, so the reader keeps their place.
  reloadListedTendersBidded({ commit, dispatch, state }, query) {
    tendersBiddedSearch += 1;
    const search = tendersBiddedSearch;
    const listed = Math.max(1, state.pagination.page);
    commit('GET_TENDERS_BIDDED');
    const pages = Array.from({ length: listed }, (_, index) => index + 1);
    return pages
      .reduce(
        (loaded, page) =>
          loaded.then((done) => {
            // A newer search took over: stop, its own request is the one to keep.
            if (search !== tendersBiddedSearch) return done;
            return fetchTendersBidded({ ...query, page }, state).then(({ data, pagination }) => ({
              data: withoutListed(done.data, data),
              pagination,
            }));
          }),
        Promise.resolve({ data: [], pagination: null }),
      )
      .then(({ data, pagination }) => {
        if (search !== tendersBiddedSearch) return;
        commit('GET_TENDERS_BIDDED_SUCCESS', { data, pagination: { ...pagination, page: listed } });
        if (data.length) dispatch('getTendersBiddedMatches', { tenders: data });
        else commit('SET_TENDERS_BIDDED_MATCHES_PENDING', false);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching tenders bidded:`, error);
          throw error;
        }
      });
  },
  // Empty keywords → API defaults to all tenant keywords (coverage indicator).
  getTendersBiddedMatches({ commit }, { tenders }) {
    commit('SET_TENDERS_BIDDED_MATCHES_PENDING', true);
    const tenderIds = tenders.map((t) => t.id);
    return tendersService.getTendersMatches({ tenderIds, keywords: [] }).then(
      (tendersMatches) => {
        commit('SET_TENDERS_BIDDED_MATCHED_KEYWORDS', tendersMatches);
      },
      (error) => {
        commit('SET_TENDERS_BIDDED_MATCHES_PENDING', false);
        if (!axios.isCancel(error)) {
          console.error(`Error fetching tenders matched keywords:`, error);
        }
      },
    );
  },
  async getCompetitor({ commit }, id) {
    commit('GET_COMPETITOR');

    tenantSettingsService
      .getCompetitor(id)
      .then((response) => {
        commit('GET_COMPETITOR_SUCCESS', response);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching competitors:`, error);
          throw error;
        }
      });
  },
  async getCompetitors({ commit }, { page, sortingFieldIn, sortingOrderIn }) {
    commit('GET_COMPETITORS');

    const sortingField = sortingFieldIn ? sortingFieldIn : state.sortingField;
    const sortingOrder = sortingOrderIn ? sortingOrderIn : state.sortingOrder;

    // we load the list of tenants first because this endpoint is lighter
    // then we call the BI endpoint and merge the competitor values
    if (page === 1) {
      tenantSettingsService
        .getCompetitors(sortingField, sortingOrder)
        .then((competitors) => {
          commit('GET_COMPETITORS_SUCCESS', competitors);
          businessIntelligenceService
            .getCompetitors(page, sortingField, sortingOrder)
            .then(({ data, pagination }) => {
              let mergedData = [];
              competitors.forEach((competitor) => {
                const matchedCompetitor = data.find((c) => c.id === competitor.id);
                mergedData.push({ ...competitor, ...matchedCompetitor });
              });

              commit('GET_COMPETITORS_DETAILED_SUCCESS', {
                competitors: mergedData,
                pagination,
              });
            })
            .catch((error) => {
              if (!axios.isCancel(error)) {
                console.error(`Error fetching competitors detailed:`, error);
                throw error;
              }
            });
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.error(`Error fetching competitors:`, error);
            throw error;
          }
        });
    } else {
      commit('GET_COMPETITORS');
      businessIntelligenceService
        .getCompetitors(page, sortingField, sortingOrder)
        .then(({ data, pagination }) => {
          let mergedData = [];
          state.competitors.forEach((competitor) => {
            const matchedCompetitor = data.find((c) => c.id === competitor.id);
            mergedData.push({ ...competitor, ...matchedCompetitor });
          });

          commit('GET_COMPETITORS_DETAILED_SUCCESS', {
            competitors: mergedData,
            pagination,
          });
        })
        .catch((error) => {
          if (!axios.isCancel(error)) {
            console.error(`Error fetching competitors detailed:`, error);
            throw error;
          }
        });
    }
  },

  async getAllCompetitors({ state, commit }) {
    if (state.allCompetitorsLoaded) return;
    // Share the in-flight request unless the list was invalidated after it started.
    if (allCompetitorsRequest && allCompetitorsRequestGeneration === state.competitorsGeneration) {
      return allCompetitorsRequest;
    }

    const generation = state.competitorsGeneration;
    commit('GET_ALL_COMPETITORS');
    const request = tenantSettingsService
      .getCompetitors('name', 'asc')
      .then((competitors) => {
        commit('GET_ALL_COMPETITORS_SUCCESS', { competitors, generation });
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching competitors:`, error);
          commit('ERROR', error);
          throw error;
        }
      })
      .then(
        () => {
          if (allCompetitorsRequest === request) allCompetitorsRequest = null;
        },
        (error) => {
          if (allCompetitorsRequest === request) allCompetitorsRequest = null;
          throw error;
        },
      );
    allCompetitorsRequest = request;
    allCompetitorsRequestGeneration = generation;
    return request;
  },

  // Emitters: a catalogue shared by every tenant (aloha.bi /api/v1/emitter); the
  // list is sorted and paginated by the API, so every change refetches it.
  async getEmitters({ commit }, { page = 1, sortingField = 'name', sortingOrder = 'asc', search = '' } = {}) {
    emittersSearch += 1;
    commit('GET_EMITTERS');
    return businessIntelligenceService
      .getEmitters(page, sortingField, sortingOrder, search)
      .then(({ data, pagination }) => {
        commit('GET_EMITTERS_SUCCESS', { emitters: data, pagination });
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching emitters:`, error);
          commit('GET_EMITTERS_ERROR', error);
          throw error;
        }
      });
  },
  // Adds the next page under the listed emitters (a phone's list). Not `emittersPending`: the list
  // stays usable while it loads, and a failure keeps what's listed.
  loadMoreEmitters({ commit, state }, { search = '' } = {}) {
    commit('LOAD_MORE_EMITTERS');
    return businessIntelligenceService.getEmitters(state.emittersPagination.page + 1, 'name', 'asc', search).then(
      (values) => commit('APPEND_EMITTERS', values),
      (error) => {
        // A new search cancels it, and replaces the list anyway.
        if (axios.isCancel(error)) commit('LOAD_MORE_EMITTERS_CANCELLED');
        else commit('LOAD_MORE_EMITTERS_FAILED', error);
      },
    );
  },
  // Reloads every page a phone's list shows, one after the other, so the reader keeps their place.
  reloadListedEmitters({ commit, state }, { search = '' } = {}) {
    emittersSearch += 1;
    const reload = emittersSearch;
    const listed = Math.max(1, state.emittersPagination.page);
    commit('GET_EMITTERS');
    const pages = Array.from({ length: listed }, (_, index) => index + 1);
    return pages
      .reduce(
        (loaded, page) =>
          loaded.then((done) => {
            // A newer search took over, or a deletion left fewer pages: stop.
            if (reload !== emittersSearch || (done.pagination && page > done.pagination.totalPages)) return done;
            return businessIntelligenceService
              .getEmitters(page, 'name', 'asc', search)
              .then(({ data, pagination }) => ({ emitters: withoutListed(done.emitters, data), pagination }));
          }),
        Promise.resolve({ emitters: [], pagination: null }),
      )
      .then(({ emitters, pagination }) => {
        if (reload !== emittersSearch) return;
        commit('GET_EMITTERS_SUCCESS', { emitters, pagination });
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching emitters:`, error);
          commit('GET_EMITTERS_ERROR', error);
          throw error;
        }
      });
  },
  async getEmitter({ commit }, id) {
    commit('GET_EMITTER', { id });
    return businessIntelligenceService
      .getEmitter(id)
      .then((emitter) => {
        commit('GET_EMITTER_SUCCESS', emitter);
        return emitter;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching emitter:`, error);
          commit('EMITTER_ERROR', error);
          throw error;
        }
      });
  },
  async getEmitterPerformanceSummary({ commit }, { emitterId, startDate, endDate }) {
    commit('GET_EMITTER_PERFORMANCE_SUMMARY');
    return businessIntelligenceService
      .getEmitterPerformanceSummary(emitterId, startDate, endDate)
      .then((summary) => {
        commit('GET_EMITTER_PERFORMANCE_SUMMARY_SUCCESS', summary);
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching emitter performance summary:`, error);
          commit('ERROR', error);
          throw error;
        }
      });
  },
  async addEmitter({ commit }, { name, normalizedNames }) {
    commit('ADD_EMITTER');
    return businessIntelligenceService
      .createEmitter({ name, normalizedNames })
      .then((emitter) => {
        commit('ADD_EMITTER_SUCCESS', emitter);
        return emitter;
      })
      .catch((error) => {
        console.error(`Error adding emitter:`, error);
        commit('EMITTER_ERROR', error);
        throw error;
      });
  },
  async editEmitter({ commit }, { id, name, normalizedNames }) {
    commit('EDIT_EMITTER');
    return businessIntelligenceService
      .updateEmitter(id, { name, normalizedNames })
      .then((emitter) => {
        commit('EDIT_EMITTER_SUCCESS', emitter);
        return emitter;
      })
      .catch((error) => {
        console.error(`Error editing emitter:`, error);
        commit('EMITTER_ERROR', error);
        throw error;
      });
  },
  async deleteEmitter({ commit }, id) {
    commit('DELETE_EMITTER');
    return businessIntelligenceService
      .deleteEmitter(id)
      .then((response) => {
        commit('DELETE_EMITTER_SUCCESS', id);
        return response;
      })
      .catch((error) => {
        console.error(`Error deleting emitter:`, error);
        commit('EMITTER_ERROR', error);
        throw error;
      });
  },
  // Alias suggestions for an emitter; the dialog tracks its own loading flag, so
  // the shared `pending` used by the competitor dialogs is left alone.
  async getEmitterNameSuggestion(context, name) {
    return businessIntelligenceService
      .getEmitterNameSuggestion(name)
      .then((response) => response)
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching emitter name suggestion:`, error);
          throw error;
        }
      });
  },
  async cancelGetEmitterNameSuggestion() {
    await businessIntelligenceService.cancelEmitterNameSuggestion();
  },
  async getTenantNormalizedNameSuggestion({ commit }) {
    commit('GET_NORMALIZED_NAME_SUGGESTION');
    return businessIntelligenceService
      .getTenantNormalizedNameSuggestion()
      .then((response) => {
        commit('GET_NORMALIZED_NAME_SUGGESTION_SUCCESS');
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching tenant normalized name suggestion:`, error);
          throw error;
        }
      });
  },
  async getNormalizedNameSuggestion({ commit }, name) {
    commit('GET_NORMALIZED_NAME_SUGGESTION');
    return businessIntelligenceService
      .getNormalizedNameSuggestion(name)
      .then((response) => {
        commit('GET_NORMALIZED_NAME_SUGGESTION_SUCCESS');
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error fetching normalized name suggestion:`, error);
          throw error;
        }
      });
  },
  async cancelGetNormalizedNameSuggestion({ commit }) {
    commit('CANCEL_GET_NORMALIZED_NAME_SUGGESTION');
    await businessIntelligenceService.cancelNormalizedNameSuggestion();
  },
  async addTenantNormalizedNameMany({ commit }, normalizedNames) {
    commit('ADD_TENANT_NORMALIZED_NAMES');
    return tenantSettingsService
      .addTenantNormalizedNameMany(normalizedNames)
      .then((response) => {
        commit('ADD_TENANT_NORMALIZED_NAMES_SUCCESS');
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error adding normalized name:`, error);
          throw error;
        }
      });
  },
  async editTenantNormalizedName({ commit }, { id, normalizedName }) {
    commit('EDIT_TENANT_NORMALIZED_NAME');
    return tenantSettingsService
      .editTenantNormalizedName(id, normalizedName)
      .then((response) => {
        commit('EDIT_TENANT_NORMALIZED_NAME_SUCCESS');
        commit('tenant/UPDATE_NORMALIZED_NAME', response, { root: true });
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error editing normalized name:`, error);
          throw error;
        }
      });
  },
  async deleteTenantNormalizedName({ commit }, id) {
    commit('DELETE_TENANT_NORMALIZED_NAME');
    return tenantSettingsService
      .deleteTenantNormalizedName(id)
      .then((response) => {
        commit('DELETE_TENANT_NORMALIZED_NAME_SUCCESS');
        commit('tenant/DELETE_NORMALIZED_NAME', id, { root: true });
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error deleting normalized name:`, error);
          throw error;
        }
      });
  },
  async addCompetitor({ commit, dispatch }, competitor) {
    commit('ADD_COMPETITOR');
    await dispatch('cancelGetNormalizedNameSuggestion');
    return tenantSettingsService
      .addCompetitor(competitor)
      .then((response) => {
        commit('ADD_COMPETITOR_SUCCESS');
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error adding competitor:`, error);
          throw error;
        }
      });
  },
  async editCompetitor({ commit }, { id, name, normalizedNames, existingCompetitorNames }) {
    commit('EDIT_COMPETITOR');
    // merge the existing names with the new ones
    const mergedNames = normalizedNames.map((name) => {
      const existing = existingCompetitorNames.find((competitorName) => competitorName.normalizedName === name);
      if (existing) {
        return { id: existing.id, normalizedName: name };
      }
      return { normalizedName: name };
    });

    const payload = {
      name,
      normalizedNames: mergedNames,
    };

    return tenantSettingsService
      .editCompetitor(id, payload)
      .then((response) => {
        commit('EDIT_COMPETITOR_SUCCESS', response);
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error editing competitor:`, error);
          throw error;
        }
      });
  },
  async deleteCompetitor({ commit }, id) {
    commit('DELETE_COMPETITOR');
    return tenantSettingsService
      .deleteCompetitor(id)
      .then((response) => {
        commit('DELETE_COMPETITOR_SUCCESS', id);
        return response;
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error deleting competitor:`, error);
          throw error;
        }
      });
  },
  async getTender({ commit, state }, { id, competitorId }) {
    commit('GET_TENDER', { id });
    const requestId = state.tenderRequestId;
    return businessIntelligenceService
      .getTenderById(id, competitorId)
      .then((response) => {
        commit('GET_TENDER_SUCCESS', { requestId, data: response });
      })
      .catch((error) => {
        if (!axios.isCancel(error)) {
          console.error(`Error retrieving tender:`, error);
          commit('GET_TENDER_ERROR', { requestId, error });
          throw error;
        }
      });
  },
  setCustomSortingField({ commit }, { sortingField }) {
    commit('SET_CUSTOM_SORTING_FIELD', sortingField);
  },
  setCustomSortingOrder({ commit }, { sortingOrder }) {
    commit('SET_CUSTOM_SORTING_ORDER', sortingOrder);
  },
  setDates({ commit }, dates) {
    commit('SET_DATES', dates);
  },
  RESET({ commit }) {
    commit('RESET_STORE');
  },
};

const mutations = {
  GET_CHART(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      dataPointDataPending: false,
      resolved: false,
      rejected: false,
    };
  },
  GET_CHART_SUCCESS(state, { charts }) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      dataPointDataPending: false,
      resolved: true,
      rejected: false,
    };
    const tempCharts = [];
    charts.forEach((chart) => {
      const { id, series, chartOptions } = chart;
      const type = getChartValueType(id);

      const isCompetitorComparisonChart = !['weighted-diff-per-month', 'cumul-amount-provider'].includes(id);

      chart.series.forEach((series) => {
        if (series.name) series.name = formatSeriesName(series.name);
      });
      chart.series = sortSeries(series);
      chart.chartOptions.title = { text: splitTitleText(i18n.t(`charts.text.${id}.title`)), align: 'center' };
      chart.chartOptions.legend = { position: 'right', offsetY: 40 };
      chart.chartOptions.markers = { size: isCompetitorComparisonChart ? 1 : 0 };
      chart.chartOptions.chart.toolbar = { tools: { download: false } };
      chart.chartOptions.yaxis = {
        labels: { formatter: (value) => (type === 'money' ? (value / 1000000).toFixed(0) + ' M' : value + ' %') },
        title: { text: i18n.t(`charts.text.${id}.yaxisTitle`) },
      };

      chart.chartOptions.tooltip = {
        intersect: isCompetitorComparisonChart, // this needs to be true for the dataPointMouseEnter event to be triggered by Apexchart
        shared: false,
        enabled: true,
        ...(isCompetitorComparisonChart
          ? {
              custom: () => {
                return `<span class="chart-tooltip__container" id="tooltip-content-custom-${id}">${i18n.t(
                  'page.business-intelligence.awards.loading',
                )}</span>`;
              },
            }
          : {
              x: {
                formatter: (value) => {
                  const date = new Date(value);
                  return date.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
                },
              },
              y: {
                formatter: (value) => {
                  if (value) {
                    return type === 'money'
                      ? `${value
                          .toFixed(0)
                          .toString()
                          .replace(/\B(?=(\d{3})+(?!\d))/g, ' ')} $CAD`
                      : `${value.toFixed(2)} %`;
                  }
                },
              },
            }),
      };

      chart.chartOptions.chart.animations = { speed: 300, easing: 'linear', animateGradually: { enabled: false } };

      tempCharts.push({ id, type, series, options: chartOptions });
    });
    state.charts = tempCharts;
  },
  GET_PERFORMANCE_SUMMARY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.performanceSummary = null;
  },
  GET_PERFORMANCE_SUMMARY_SUCCESS(state, summary) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.performanceSummary = summary;
  },
  GET_COMPETITOR_PERFORMANCE_SUMMARY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.competitorPerformanceSummary = null;
  },
  GET_COMPETITOR_PERFORMANCE_SUMMARY_SUCCESS(state, summary) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.competitorPerformanceSummary = summary;
  },
  GET_TENDERS_BIDDED(state) {
    // state.tendersBidded.data = [];
    state.tendersBidded.pending = true;
    state.tendersBidded.matchesPending = true;
    state.tendersBidded.loadMoreError = null;
  },
  LOAD_MORE_TENDERS_BIDDED(state) {
    state.tendersBidded.loadingMore = true;
    state.tendersBidded.loadMoreError = null;
  },
  APPEND_TENDERS_BIDDED(state, { data, pagination }) {
    state.tendersBidded.data = withoutListed(state.tendersBidded.data, data);
    state.pagination = pagination;
    state.tendersBidded.loadingMore = false;
  },
  LOAD_MORE_TENDERS_BIDDED_FAILED(state, error) {
    state.tendersBidded.loadingMore = false;
    state.tendersBidded.loadMoreError = error;
  },
  LOAD_MORE_TENDERS_BIDDED_CANCELLED(state) {
    state.tendersBidded.loadingMore = false;
  },
  GET_TENDERS_BIDDED_SUCCESS(state, { data, pagination }) {
    state.tendersBidded.data = data;
    state.tendersBidded.pending = false;
    state.pagination = pagination;
  },
  SET_TENDERS_BIDDED_MATCHES_PENDING(state, pending) {
    state.tendersBidded.matchesPending = pending;
  },
  SET_TENDERS_BIDDED_MATCHED_KEYWORDS(state, tendersMatches) {
    state.tendersBidded.matchesPending = false;
    state.tendersBidded.data = state.tendersBidded.data.map((tender) => {
      const match = tendersMatches[tender.id] || tendersMatches[String(tender.id)];
      // A phone's next page asks only for its own tenders: the listed ones keep theirs.
      const listed = tender.matchedKeywords || [];
      return {
        ...tender,
        matchedKeywords: match ? match.matchedKeywords || [] : listed,
      };
    });
  },
  GET_COMPETITOR(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_COMPETITOR_SUCCESS(state, data) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      detailedPending: false,
      resolved: true,
      rejected: false,
    };
    state.competitor = data;
  },
  GET_COMPETITORS(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      detailedPending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_COMPETITORS_SUCCESS(state, data) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.competitors = data;
    // Different sorting than getAllCompetitors.
    invalidateCompetitors(state);
  },
  GET_ALL_COMPETITORS(state) {
    // `detailedPending` belongs to the BI competition table; this plain list fetch must not touch it.
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_ALL_COMPETITORS_SUCCESS(state, { competitors, generation }) {
    // The list changed while this request was in flight; a fresh request replaces it.
    if (generation !== state.competitorsGeneration) return;

    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.competitors = competitors;
    state.allCompetitorsLoaded = true;
  },
  GET_COMPETITORS_DETAILED_SUCCESS(state, { competitors }) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      detailedPending: false,
      resolved: true,
      rejected: false,
    };
    state.competitors = competitors;
    invalidateCompetitors(state);
  },
  GET_EMITTERS(state) {
    state.status = {
      ...state.status,
      error: null,
      emittersPending: true,
      rejected: false,
    };
    state.emittersLoadMoreError = null;
  },
  LOAD_MORE_EMITTERS(state) {
    state.emittersLoadingMore = true;
    state.emittersLoadMoreError = null;
  },
  APPEND_EMITTERS(state, { data, pagination }) {
    state.emitters = withoutListed(state.emitters, data);
    state.emittersPagination = pagination;
    state.emittersLoadingMore = false;
  },
  LOAD_MORE_EMITTERS_FAILED(state, error) {
    state.emittersLoadingMore = false;
    state.emittersLoadMoreError = error;
  },
  LOAD_MORE_EMITTERS_CANCELLED(state) {
    state.emittersLoadingMore = false;
  },
  GET_EMITTERS_SUCCESS(state, { emitters, pagination }) {
    state.status = {
      ...state.status,
      error: null,
      emittersPending: false,
      rejected: false,
    };
    state.emitters = emitters;
    state.emittersPagination = pagination;
  },
  GET_EMITTERS_ERROR(state, error) {
    state.status = {
      ...state.status,
      emittersPending: false,
      rejected: true,
      error,
    };
  },
  GET_EMITTER(state, { id } = {}) {
    // Drop another emitter's data so the page does not show it while loading.
    if (state.emitter && state.emitter.id !== id) state.emitter = null;
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_EMITTER_SUCCESS(state, emitter) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.emitter = emitter;
  },
  GET_EMITTER_PERFORMANCE_SUMMARY(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: false,
      rejected: false,
    };
    state.emitterPerformanceSummary = null;
  },
  GET_EMITTER_PERFORMANCE_SUMMARY_SUCCESS(state, summary) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
    state.emitterPerformanceSummary = summary;
  },
  ADD_EMITTER(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  ADD_EMITTER_SUCCESS(state) {
    // The list is sorted and paginated by the API: the caller refetches it.
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  EDIT_EMITTER(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  EDIT_EMITTER_SUCCESS(state, updated) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.emitters.findIndex((emitter) => emitter.id === updated.id);
    if (index !== -1) {
      // the list row shows the name and how many aliases the emitter has
      state.emitters.splice(index, 1, {
        ...state.emitters[index],
        name: updated.name,
        normalized_names_count: (updated.emitter_names || []).length,
      });
    }
    if (state.emitter && state.emitter.id === updated.id) {
      state.emitter = updated;
    }
    if (state.emitterPerformanceSummary && state.emitterPerformanceSummary.emitter) {
      state.emitterPerformanceSummary = {
        ...state.emitterPerformanceSummary,
        emitter: { ...state.emitterPerformanceSummary.emitter, name: updated.name },
      };
    }
  },
  DELETE_EMITTER(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  DELETE_EMITTER_SUCCESS(state, id) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    const index = state.emitters.findIndex((emitter) => emitter.id === id);
    if (index !== -1) {
      state.emitters.splice(index, 1);
      state.emittersPagination = {
        ...state.emittersPagination,
        totalCount: Math.max(0, state.emittersPagination.totalCount - 1),
      };
    }
    if (state.emitter && state.emitter.id === id) {
      state.emitter = null;
    }
  },
  EMITTER_ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      rejected: true,
      error,
    };
  },
  GET_NORMALIZED_NAME_SUGGESTION(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_NORMALIZED_NAME_SUGGESTION_SUCCESS(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      resolved: true,
      rejected: false,
    };
  },
  GET_TENDER(state, { id } = {}) {
    // Drop the previous tender so the panels show a spinner instead of stale data.
    state.tenderId = id || null;
    state.tenderRequestId = nextTenderRequestId++;
    if (id && state.tender && state.tender.id !== id) state.tender = null;
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      tenderPending: true,
      tenderError: null,
      resolved: false,
      rejected: false,
    };
  },
  GET_TENDER_SUCCESS(state, { requestId, data }) {
    // Response of an earlier request: another tender, a superseded refetch, or before a reset.
    if (requestId !== state.tenderRequestId) return;

    state.status = {
      ...state.status,
      error: null,
      pending: false,
      tenderPending: false,
      tenderError: null,
      resolved: true,
      rejected: false,
    };
    state.tender = data;
  },
  GET_TENDER_ERROR(state, { requestId, error }) {
    if (requestId !== state.tenderRequestId) return;

    state.status = {
      ...state.status,
      pending: false,
      tenderPending: false,
      tenderError: error,
      rejected: true,
      error,
    };
  },
  CANCEL_GET_NORMALIZED_NAME_SUGGESTION(state) {
    state.status = {
      ...state.status,
      error: null,
      pending: true,
      resolved: false,
      rejected: false,
    };
  },
  ADD_TENANT_NORMALIZED_NAMES(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  ADD_TENANT_NORMALIZED_NAMES_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  EDIT_TENANT_NORMALIZED_NAME(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  EDIT_TENANT_NORMALIZED_NAME_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  DELETE_TENANT_NORMALIZED_NAME(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  DELETE_TENANT_NORMALIZED_NAME_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
  },
  ADD_COMPETITOR(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  ADD_COMPETITOR_SUCCESS(state) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    // The new competitor is not added locally; force a refetch.
    invalidateCompetitors(state);
  },
  EDIT_COMPETITOR(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  EDIT_COMPETITOR_SUCCESS(state, updated) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    invalidateCompetitors(state);
    const index = state.competitors.findIndex((competitor) => competitor.id === updated.id);
    if (index !== -1) {
      const existingCompetitor = state.competitors[index];
      state.competitors.splice(index, 1, { ...existingCompetitor, ...updated });
    }
  },
  DELETE_COMPETITOR(state) {
    state.status = {
      ...state.status,
      pending: true,
      resolved: false,
    };
  },
  DELETE_COMPETITOR_SUCCESS(state, id) {
    state.status = {
      ...state.status,
      pending: false,
      resolved: true,
    };
    invalidateCompetitors(state);
    const index = state.competitors.findIndex((competitor) => competitor.id === id);
    if (index !== -1) {
      state.competitors.splice(index, 1);
    }
  },
  SET_DATES(state, dates) {
    state.dates = dates;
  },
  ERROR(state, error) {
    state.status = {
      ...state.status,
      pending: false,
      dataPointDataPending: false,
      rejected: true,
      error,
    };
  },
  RESET_STORE(state) {
    const initial = initialState();
    Object.keys(initial).forEach((key) => {
      state[key] = initial[key];
    });
  },
  GET_DATAPOINT_VALUES(state) {
    state.status = {
      error: null,
      pending: false,
      dataPointDataPending: true,
      resolved: false,
      rejected: false,
    };
  },
  GET_DATAPOINT_VALUES_SUCCESS(state, values) {
    state.status = {
      ...state.status,
      error: null,
      pending: false,
      dataPointDataPending: false,
      resolved: true,
      rejected: false,
    };
    state.dataPointValues = values;
  },
  SET_CUSTOM_SORTING_FIELD(state, sortingField) {
    state.sortingField = sortingField;
  },
  SET_CUSTOM_SORTING_ORDER(state, sortingOrder) {
    state.sortingOrder = sortingOrder;
  },
  RESET_PAGINATION(state) {
    state.pagination = DEFAULT_PAGINATION;
  },
};

const getters = {
  charts: (state) => state.charts,
  dataPointValues: (state) => state.dataPointValues,
  performanceSummary: (state) => state.performanceSummary,
  competitorPerformanceSummary: (state) => state.competitorPerformanceSummary,
  tender: (state) => state.tender,
  tendersBidded: (state) => state.tendersBidded.data,
  competitors: (state) => state.competitors,
  competitor: (state) => state.competitor,
  emitters: (state) => state.emitters,
  emitter: (state) => state.emitter,
  emitterPerformanceSummary: (state) => state.emitterPerformanceSummary,
  emittersPending: (state) => state.status.emittersPending,
  emittersPage: (state) => state.emittersPagination.page,
  emittersTotalCount: (state) => state.emittersPagination.totalCount,
  emittersTotalPages: (state) => state.emittersPagination.totalPages,
  emittersLoadingMore: (state) => state.emittersLoadingMore,
  emittersLoadMoreError: (state) => state.emittersLoadMoreError,

  // status
  error: (state) => state.status.error,
  pending: (state) => state.status.pending,
  tenderPending: (state) => state.status.tenderPending,
  tenderError: (state) => state.status.tenderError,
  dataPointDataPending: (state) => state.status.dataPointDataPending,
  rejected: (state) => state.status.rejected,
  resolved: (state) => state.status.resolved,
  detailedPending: (state) => state.status.detailedPending,
  tendersBiddedPending: (state) => state.tendersBidded.pending,
  matchesPending: (state) => state.tendersBidded.matchesPending,
  tendersBiddedLoadingMore: (state) => state.tendersBidded.loadingMore,
  tendersBiddedLoadMoreError: (state) => state.tendersBidded.loadMoreError,

  // Pagination
  page: (state) => state.pagination.page,
  totalCount: (state) => state.pagination.totalCount,
  totalPages: (state) => state.pagination.totalPages,
  dates: (state) => state.dates,
};

function getChartValueType(id) {
  return id.includes('amount') ? 'money' : 'percentage';
}
function formatId(id) {
  return id.replace(/_/g, '-');
}

function formatSeriesName(seriesName) {
  const match = seriesName.match(/\b\d{4}\b/);
  return match ? match[0] : seriesName;
}

function sortSeries(seriesArray) {
  return seriesArray.every((series) => /\b\d{4}\b/.test(series.name))
    ? seriesArray.sort((a, b) => b.name.localeCompare(a.name))
    : seriesArray;
}
function splitTitleText(title) {
  const words = title.split(' ');
  const chunkSize = 6;
  const chunks = [];
  for (let i = 0; i < words.length; i += chunkSize) {
    chunks.push(words.slice(i, i + chunkSize).join(' '));
  }
  return chunks;
}

export const biDashboard = {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
